import { z } from "zod";

/**
 * Shared DTO cho Admin Abuse Report & Content Moderation (US-AE12.F2-0043 · @UC9111
 * user-abuse-reports · @UC9112 content-moderation). MỘT nguồn sự thật cho api (S02), worker
 * flag job (S05), admin web (S04).
 *
 * AbuseReport = báo cáo lạm dụng từ user → queue T&S theo SLA (slaBreached derived). ModerationItem =
 * nội dung (tên nhóm/quỹ/note/nickname) khớp blocklist/rule → review + takedown (hide/replace).
 *
 * BẤT BIẾN (invariants):
 * - pii (C2): reporterUserId/subjectId là con trỏ (id) — KHÔNG tên/email/phone. contentSnippet giới hạn
 *   (reviewer thấy để quyết); audit dùng contentHash thay raw. KHÔNG field "translated/dịch" (PRD i18n
 *   KHÔNG đụng user data).
 * - G2: KHÔNG field ghi/sửa tiền — chỉ báo cáo + kiểm duyệt hiển thị.
 * - `.strict()` cho body takedown — chống over-post/field lạ. reason bắt buộc non-empty (AC1).
 * - Cursor envelope `{ data, pageInfo }` DRY (nhất quán admin-trust-review + contracts hiện hữu).
 */

// ============================================================
// Enum-lite (khớp badge STATUS admin + mock resources.ts abuse/moderation)
// ============================================================

/** Đối tượng bị báo cáo (mock resources.ts abuse trỏ group hoặc user; fund theo story). */
export const abuseSubjectTypeSchema = z.enum(["group", "user", "fund"]);
export type AbuseSubjectType = z.infer<typeof abuseSubjectTypeSchema>;

/** Trạng thái vòng đời báo cáo (queue T&S). open = mới; triaged = đang xử lý; closed = đã đóng. */
export const abuseReportStatusSchema = z.enum(["open", "triaged", "closed"]);
export type AbuseReportStatus = z.infer<typeof abuseReportStatusSchema>;

/** Nơi nội dung vi phạm (khớp mock resources.ts moderation kind: tên nhóm/quỹ/ghi chú/nickname). */
export const moderationFieldSchema = z.enum(["group_name", "fund_name", "note", "nickname"]);
export type ModerationField = z.infer<typeof moderationFieldSchema>;

/** Trạng thái mục kiểm duyệt. flagged = chờ review; cleared = duyệt sạch; takendown = đã gỡ/thay. */
export const moderationStatusSchema = z.enum(["flagged", "cleared", "takendown"]);
export type ModerationStatus = z.infer<typeof moderationStatusSchema>;

/** Hành động takedown. hide = ẩn nội dung; replace = thay bằng replacementText (AC3). */
export const takedownActionSchema = z.enum(["hide", "replace"]);
export type TakedownAction = z.infer<typeof takedownActionSchema>;

// ============================================================
// Abuse report (list)
// ============================================================

/**
 * 1 dòng báo cáo lạm dụng. reporterUserId/subjectId = id (con trỏ — resolve tên ở UI ref). slaBreached
 * = derived (slaDueAt < now && status ∈ {open,triaged}) — server tính, UI hiển thị badge.
 */
export const abuseReportSchema = z
  .object({
    id: z.string(),
    code: z.string(),
    reporterUserId: z.string(),
    /** Display-name người báo cáo (T01 · @UC9135): username(social)→email→phone. null nếu không resolve. */
    reporterName: z.string().nullable(),
    subjectType: abuseSubjectTypeSchema,
    subjectId: z.string(),
    /** Display-name đối tượng bị báo cáo (T01): group→tên nhóm; user→username/email/phone; fund→tên quỹ. */
    subjectName: z.string().nullable(),
    reason: z.string(),
    status: abuseReportStatusSchema,
    slaDueAt: z.string().nullable(),
    slaBreached: z.boolean(),
    createdAt: z.string(),
  })
  .strict();

export type AbuseReport = z.infer<typeof abuseReportSchema>;

/** Envelope list báo cáo: `{ data, pageInfo }` (cursor DRY). */
export const abuseReportListResponseSchema = z
  .object({
    data: z.array(abuseReportSchema),
    pageInfo: z
      .object({
        hasNext: z.boolean(),
        nextCursor: z.string().nullable(),
      })
      .strict(),
  })
  .strict();

export type AbuseReportListResponse = z.infer<typeof abuseReportListResponseSchema>;

// ============================================================
// Moderation item (list)
// ============================================================

/**
 * 1 mục kiểm duyệt. contentSnippet = trích đoạn nội dung (reviewer thấy để quyết, ≤200 char, KHÔNG
 * dịch). contentHash = hash đầy đủ (audit dùng). matchedBlocklist null khi flag thủ công. action/
 * decidedBy/decidedAt null khi chưa takedown.
 */
export const moderationItemSchema = z
  .object({
    id: z.string(),
    code: z.string(),
    field: moderationFieldSchema,
    subjectId: z.string(),
    /** Display-name đối tượng (T01): field group_name→tên nhóm; fund_name→tên quỹ; note/nickname→user. */
    subjectName: z.string().nullable(),
    contentSnippet: z.string(),
    contentHash: z.string(),
    matchedBlocklist: z.string().nullable(),
    status: moderationStatusSchema,
    action: takedownActionSchema.nullable(),
    decidedBy: z.string().nullable(),
    decidedAt: z.string().nullable(),
    createdAt: z.string(),
  })
  .strict();

export type ModerationItem = z.infer<typeof moderationItemSchema>;

/** Envelope list kiểm duyệt: `{ data, pageInfo }` (cursor DRY). */
export const moderationListResponseSchema = z
  .object({
    data: z.array(moderationItemSchema),
    pageInfo: z
      .object({
        hasNext: z.boolean(),
        nextCursor: z.string().nullable(),
      })
      .strict(),
  })
  .strict();

export type ModerationListResponse = z.infer<typeof moderationListResponseSchema>;

// ============================================================
// Query (GET /admin/abuse-reports · GET /admin/moderation/items)
// ============================================================

export const TRUST_LIST_MAX_LIMIT = 100;

/** Query list báo cáo — filter status, cursor + limit. `.strict()` chặn param lạ. */
export const abuseReportListQuerySchema = z
  .object({
    status: abuseReportStatusSchema.optional(),
    cursor: z.string().max(512).optional(),
    limit: z.coerce.number().int().min(1).max(TRUST_LIST_MAX_LIMIT).optional(),
  })
  .strict();

export type AbuseReportListQuery = z.infer<typeof abuseReportListQuerySchema>;

/** Query list kiểm duyệt — filter status (default flagged ở service), cursor + limit. */
export const moderationListQuerySchema = z
  .object({
    status: moderationStatusSchema.optional(),
    cursor: z.string().max(512).optional(),
    limit: z.coerce.number().int().min(1).max(TRUST_LIST_MAX_LIMIT).optional(),
  })
  .strict();

export type ModerationListQuery = z.infer<typeof moderationListQuerySchema>;

// ============================================================
// Takedown (POST /admin/moderation/items/:id/takedown)
// ============================================================

export const TAKEDOWN_REASON_MIN_LENGTH = 1;
export const TAKEDOWN_REASON_MAX_LENGTH = 2000;
export const TAKEDOWN_REPLACEMENT_MAX_LENGTH = 500;

/**
 * Body takedown (AC1/AC3). action ∈ {hide,replace}; reason BẮT BUỘC non-empty (mọi takedown phải có lý
 * do — audit). replacementText BẮT BUỘC khi action=replace, CẤM khi action=hide (refine). `.strict()`
 * chống over-post. KHÔNG field ghi/sửa tiền (G2). Notify owner WIRE ở service (reuse notifications).
 */
export const takedownBodySchema = z
  .object({
    action: takedownActionSchema,
    reason: z.string().trim().min(TAKEDOWN_REASON_MIN_LENGTH).max(TAKEDOWN_REASON_MAX_LENGTH),
    replacementText: z.string().trim().max(TAKEDOWN_REPLACEMENT_MAX_LENGTH).optional(),
  })
  .strict()
  .refine((v) => v.action !== "replace" || (v.replacementText != null && v.replacementText.length > 0), {
    message: "replacementText bắt buộc khi action=replace",
    path: ["replacementText"],
  })
  .refine((v) => v.action !== "hide" || v.replacementText == null, {
    message: "replacementText không áp dụng khi action=hide",
    path: ["replacementText"],
  });

export type TakedownBody = z.infer<typeof takedownBodySchema>;

// ============================================================
// Blocklist config (config-driven qua system_settings) — MỘT nguồn sự thật cho api (S02) matcher +
// worker flag job (S05). [Decision Record SD-9: sensible-default version-1, PO backfill docs].
// KHÔNG hardcode danh sách trong code — đọc động từ settings. SLA report queue cũng config-driven.
// ============================================================

/**
 * Key system_settings cho blocklist + SLA. Đọc động lúc chạy — đổi qua endpoint system-settings
 * (SUPER_ADMIN + audit) có hiệu lực lần quét/tạo sau (S05 AC3).
 *  - moderation_blocklist: JSON array từ khoá cấm (VN có/không dấu — matcher normalize bỏ dấu).
 *  - abuse_report_sla_hours: số giờ SLA cho báo cáo (slaDueAt = createdAt + N giờ).
 */
export const TRUST_MODERATION_KEYS = {
  moderationBlocklist: "trust.moderation_blocklist",
  abuseReportSlaHours: "trust.abuse_report_sla_hours",
} as const;

/**
 * Default sensible version-1 (SD-9). Đổi CHỈ qua system-settings.
 *  - blocklist: danh sách tối thiểu khớp mock rule ("quỹ đen") + vài từ thô tục hiển nhiên. KHÔNG bịa
 *    danh sách dài — T&S/PO backfill. Matcher normalize (bỏ dấu + lowercase) nên "quy den" cũng khớp.
 *  - SLA 24h: hạn xử lý báo cáo lạm dụng MVP (T&S review trong 1 ngày).
 */
export const TRUST_MODERATION_DEFAULTS = {
  moderationBlocklist: ["quỹ đen", "lừa đảo", "đm", "địt", "lồn"] as readonly string[],
  abuseReportSlaHours: 24,
} as const;
